export class Device {
  refId: string;
  profileId: string;
  fkPlace: number;
  fkRoom: string | null;
  mdmDeviceData: MdmDeviceData | null;
  hostInstance: string;
  lastSeen: Date | null;
  serialNumber: string;
  roomNumber: string | null;
  doorNumber: string | null;
  pmsNumber: string | null;
  deviceName: string;
  lastWipeOn: Date | null;

  constructor(dto: any) {
    this.refId = dto.MdmDevice.REFID;
    this.profileId = dto.MdmDevice.ProfileId;
    this.fkPlace = dto.MdmDevice.FKPlace;
    this.fkRoom = dto.MdmDevice.FKRoom;
    this.mdmDeviceData = dto.MdmDevice.MdmData ? new MdmDeviceData(dto.MdmDevice.MdmData) : null;
    this.hostInstance = dto.MdmDevice.HostInstance;
    this.lastSeen = dto.MdmDevice.LastSeen ? new Date(dto.MdmDevice.LastSeen) : null;
    this.serialNumber = dto.MdmDevice.SerialNumber;
    this.roomNumber = dto.RoomNumber;
    this.doorNumber = dto.DoorNumber;
    this.pmsNumber = dto.PmsNumber;
    this.deviceName = dto.DeviceName ??
      dto?.MdmDeviceData?.DisplayName ??
      dto.mdmDeviceData?.assetTag?.length > 1
      ? dto.mdmDeviceData?.assetTag
      : dto.mdmDeviceData?.deviceName ?? 'N/A';
    this.lastWipeOn = dto.LastWipeOn ? new Date(dto.LastWipeOn) : null;
  }
}

export class MdmDeviceData {
  id: string;
  serialNumber: string;
  displayName: string;
  ipAddress: string;
  osType: string;
  osVersion: string;
  roomNumber: string | null;
  placeId: string;
  type: string;
  udid: string;
  wifiMacAddress: string;
  managementId: string | number | null;
  model: string;
  lastCheckIn: Date | null;
  isSupervised: boolean;

  constructor(dto: any) {
    this.id = dto.Id;
    this.serialNumber = dto.SerialNumber;
    this.displayName = dto.DisplayName;
    this.ipAddress = dto.IpAddress;
    this.osType = dto.OsType;
    this.osVersion = dto.OsVersion;
    this.roomNumber = dto.RoomNumber;
    this.placeId = dto.PlaceId;
    this.type = dto.Type;
    this.udid = dto.Udid;
    this.wifiMacAddress = dto.WifiMacAddress;
    this.managementId = dto.ManagementId;
    this.model = dto.Model;
    this.lastCheckIn = dto.LastCheckIn ? new Date(dto.LastCheckIn) : null;
    this.isSupervised = dto.IsSupervised;
  }
}

export interface DeviceActivity {
  id: string;
  deviceId: string;
  activityType: string;
  description: string;
  applicationName: string | null;
  version: string | null;
  developer: string | null;
  bundleId: string | null;
  commandIssued: Date | null;
  commandCompleted: Date | null;
  status: "completed" | "pending" | "failed";
  createdAt: Date;
}

export class MdmDeviceCommand {
  status: string;
  name: string;
  time: string;
  index?: number;
  // for sorting purposes
  private _timeDate: Date;

  constructor(params: any, index?: number) {
    const rawTime: string | undefined = params?.time;
    this.status = params?.status;
    this.name = params?.name;
    this.index = index;

    if (rawTime) {
      const parsedDate = new Date(rawTime);
      if (!isNaN(parsedDate.getTime())) {
        this._timeDate = parsedDate;
        this.time = this.formatDate(parsedDate);
      } else {
        // Fallback for invalid date values
        this._timeDate = new Date(0);
        this.time = '';
      }
    } else {
      // Fallback when no time is provided
      this._timeDate = new Date(0);
      this.time = '';
    }
  }

  formatDate(date: string | Date | undefined): string {
    if (!date) {
      return '';
    }

    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return dateObj.toLocaleString('en-US');
  }

  getTimeDate(): Date {
    return this._timeDate;
  }
}

export class MdmDeviceCommandHistory {
  commands: MdmDeviceCommand[];
  constructor(params: any) {
    let index = 0;
    this.commands = params?.mobile_device_history?.commands?.map(
      (command: any) => new MdmDeviceCommand(command, index++)
    );
    this.commands?.sort(
      // sort by date in descending order (newest first)
      (a, b) => b.getTimeDate().getTime() - a.getTimeDate().getTime()
    );
  }
}

export type DeviceAction = "restart" | "reset" | "unassign" | "checkin" | "blank-push";

export interface AddAndAssignDeviceRequest {
  deviceSerialNumber: string;
  roomRefId?: string;
  roomNumber?: string;
}

export interface ExtractSerialResponse {
  serialNumber: string;
  confidence: number;
}
